import {
  Box,
  Button,
  DataList,
  Flex,
  SkeletonText,
  Spinner,
  SvgIcon
} from '@volue/wave-react';
import { isNil } from 'ramda';
import React, { useEffect } from 'react';

import { CollapsibleError } from '@/app/components/App/CollapsibleError';
import useFeatureToggles from '@/common/hooks/feature-toggles';
import { useFeatureDetailsV2 } from '@/features/execute-calculation-set/components/Calculation/GridElementsCalculationTacticResults/useFeatureDetails';
import { InlineNotification } from '@/features/execute-calculation-set/components/Calculation/InlineNotification';
import { ShareExecutionButton } from '@/features/execute-calculation-set/components/Calculation/components/CalculationHeader';
import type { DomainGuid } from '@/features/execute-calculation-set/overmind/calculation';
import {
  getErrorWithDetails,
  isCalculationTacticResultUnavailable
} from '@/features/execute-calculation-set/overmind/calculation/actions';
import { useActions, useAppState } from '@/overmind';
import type { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

import { useSelectedFeature } from '../../hooks/useSelectedFeature';
import { CalculationSetSubPanel } from './CalculationSetSubPanel';
import { SaveCalculationSetExecutionReport } from './SaveCalculationSetExecutionReport';
import { useNavigateOnCalculationResult } from './useNavigationOnCalculationResult';

const attributeLabelStyling = {
  color: '$colorGray50',
  fontWeight: 'normal'
};

type SelectedFeaturePanelProps = {
  onClose: () => void;
};

export function SelectedFeaturePanel({ onClose }: SelectedFeaturePanelProps) {
  const [title, setTitle] = React.useState<string | undefined>();
  const { runCalculation, shareCalculationSetExecution } =
    useActions().calculation;
  const featureFlags = useFeatureToggles();
  const isShowingGridElementTitleEnabled =
    featureFlags.isShowingGridElementTitleEnabled();

  const calculationState = useAppState().calculation.calculationTactic;
  const subtypeLabels = useAppState().arcgis.featureTypes.value ?? {};

  const { selectedFeature } = useSelectedFeature();

  const globalId = useAppState().selectedFeature.current?.gc.globalId;
  const isElementPartOfTheCalculation = globalId !== undefined;

  useNavigateOnCalculationResult();

  if (!selectedFeature) return null;

  return (
    <span
      data-component={SelectedFeaturePanel.name}
      style={{ display: 'contents' }}
    >
      <article className="pAl">
        <Flex main="space-between">
          <h3 className="delta accent120">
            {isShowingGridElementTitleEnabled
              ? title || <SkeletonText lines={1} width={'50%'} />
              : selectedFeature.gc.title ??
                subtypeLabels[selectedFeature.gc.type as number] ??
                'Unknown'}
          </h3>
          <Button
            size="small"
            withLoneIcon
            variant="ghost"
            onClick={() => onClose()}
          >
            <SvgIcon iconName="close" />
          </Button>
        </Flex>
        <GridElementDetails
          globalId={selectedFeature.gc.globalId}
          useTitle={setTitle}
        />
        <Box className="ofA">
          <DataList align="right" className="mAn pBl">
            <DataList.Row>
              <DataList.Label css={attributeLabelStyling}>
                {'Asset Group:'}
              </DataList.Label>
              <DataList.Value>
                {subtypeLabels[selectedFeature.gc.type as number] ??
                  selectedFeature.gc.type ??
                  'Unknown'}
              </DataList.Value>
            </DataList.Row>
          </DataList>
          {isElementPartOfTheCalculation && (
            <CalculationSetSubPanel
              runTactic={tacticId =>
                runCalculation({
                  id: selectedFeature.gc.globalId,
                  tacticId
                })
              }
            />
          )}

          {calculationState.current === 'ERROR' ||
            (calculationState.current === 'LOADED' &&
              isCalculationTacticResultUnavailable(calculationState.value) && (
                <Flex className="w100" flow={'column'}>
                  {
                    <Flex className="w100" flow="column">
                      <InlineNotification
                        type="ERROR"
                        className="mAl jcC"
                        content={
                          <CollapsibleError
                            error={getErrorWithDetails(calculationState)}
                          />
                        }
                        actionButtons={[
                          <ShareExecutionButton
                            className="w100"
                            buttonClassName="w100"
                            shareCalculationSetExecution={
                              shareCalculationSetExecution
                            }
                          />,
                          <SaveCalculationSetExecutionReport
                            text="Save error report as JSON"
                            variant="outline"
                          />
                        ]}
                      />
                    </Flex>
                  }
                </Flex>
              ))}
        </Box>
      </article>
    </span>
  );
}
export function useMountFeatureDetails({
  detailsOf: selectedFeature,
  mountTo: elementRef
}: {
  detailsOf: FeatureWithIDs<unknown> | undefined;
  mountTo: React.MutableRefObject<HTMLDivElement | undefined>;
}) {
  React.useEffect(() => {
    // @ts-ignore
    if (isNil(elementRef) || isNil(elementRef.current)) return;
    if (!selectedFeature) {
      clearContent(elementRef);
    }
    const featureDetails = document.querySelector('#feature');
    if (hasNoContent(elementRef) && featureDetails) {
      elementRef.current?.appendChild(featureDetails);
    }
  }, [elementRef, selectedFeature]);
}

function hasNoContent(
  divRef: React.MutableRefObject<HTMLDivElement | undefined>
) {
  return divRef.current?.innerHTML === '';
}

function clearContent(
  divRef: React.MutableRefObject<HTMLDivElement | undefined>
) {
  // @ts-ignore
  divRef.current.innerHTML = '';
}

export const GridElementDetails: React.FunctionComponent<{
  globalId: DomainGuid;
  useTitle: (title: string | undefined) => void;
}> = ({ globalId, useTitle }) => {
  const featureFlags = useFeatureToggles();
  const featureDetailsRef = React.useRef(null);
  const featureDetails = useFeatureDetailsV2({
    of: globalId,
    displayIn: featureDetailsRef
  });

  // if (isNil(featureDetails)) return null;
  if (featureFlags.isShowingGridElementTitleEnabled()) {
    useTitleWhenAvailable(featureDetails, useTitle);
  }
  return (
    <Flex flow="column" justifyContent={'center'} alignItems="center">
      {featureDetails.status === 'LOADING' && (
        <Flex alignItems="center" css={{ marginTop: '5rem' }}>
          Loading grid element details... <Spinner inline size="small" />
        </Flex>
      )}
      {featureDetails.status === 'LOADED' && (
        <div ref={featureDetailsRef} style={{ width: '100%' }} />
      )}
      {featureDetails.status === 'CANNOT_BE_LOADED' && (
        <Flex alignItems="center" css={{ paddingY: '$spacingXxxl' }}>
          Details cannot be loaded.
        </Flex>
      )}
    </Flex>
  );
};

function useTitleWhenAvailable(
  featureDetails: NonNullable<ReturnType<typeof useFeatureDetailsV2>>,
  useTitle: (title: string | undefined) => void
) {
  useEffect(() => {
    if (featureDetails.status === 'LOADED' && !featureDetails.title) {
      return;
    }
    useTitle(featureDetails.title);
  }, [featureDetails]);
}
